import { IconCheckDefault, IconShieldCheck } from '@iwoca/icons';
import { Pill, Text } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import creditCardHandImage from './images/credit-card-hand.webp';

type InterstitialOfferProps = {
    onAccept: () => void;
    onReject: () => void;
};

export const InterstitialOffer = ({
    onAccept,
    onReject
}: InterstitialOfferProps) => {
    return (
        <div className="shadow-sm mt-l overflow-hidden rounded-xl border border-structure-80 bg-white text-left">
            <div className="relative overflow-hidden p-l pb-xl">
                <img
                    src={creditCardHandImage}
                    alt="Credit card"
                    className="absolute right-0 top-0 hidden h-full max-w-none object-contain object-right-top min-sm:block"
                />

                <Pill variant="green" size="small" className="mb-xs">
                    <FormattedMessage
                        id="_.credit_card_waitlist.badge"
                        defaultMessage="You're eligible"
                    />
                </Pill>
                <Text className="mb-s text-xl font-bold">
                    <FormattedMessage
                        id="_.credit_card_waitlist.title"
                        defaultMessage="Early access to the iwoca Credit Card"
                    />
                </Text>

                <div className="space-y-xs">
                    <div className="flex items-start gap-xs">
                        <IconCheckDefault className="mt-xxs text-success-drk" />
                        <Text className="text-s">
                            <FormattedMessage
                                id="_.credit_card_waitlist.benefit_1"
                                defaultMessage="1% cashback on every transaction"
                            />
                        </Text>
                    </div>
                    <div className="flex items-start gap-xs">
                        <IconCheckDefault className="mt-xxs text-success-drk" />
                        <Text className="text-s">
                            <FormattedMessage
                                id="_.credit_card_waitlist.benefit_2"
                                defaultMessage="Up to £250,000 credit limit"
                            />
                        </Text>
                    </div>
                    <div className="flex items-start gap-xs">
                        <IconCheckDefault className="mt-xxs text-success-drk" />
                        <Text className="text-s">
                            <FormattedMessage
                                id="_.credit_card_waitlist.benefit_3"
                                defaultMessage="Up to 62 days interest-free"
                            />
                        </Text>
                    </div>
                    <div className="flex items-start gap-xs">
                        <IconCheckDefault className="mt-xxs text-success-drk" />
                        <Text className="text-s">
                            <FormattedMessage
                                id="_.credit_card_waitlist.benefit_4"
                                defaultMessage="No FX, annual or late fees"
                            />
                        </Text>
                    </div>
                </div>
            </div>

            <hr className="mb-m border-t border-structure-95" />

            <div className="flex gap-s px-l">
                <button
                    onClick={onReject}
                    className="flex-1 rounded-full border-2 bg-white px-l py-m text-m font-med transition-colors hover:bg-structure-95"
                    style={{ borderColor: '#07172b', color: '#07172b' }}
                >
                    <FormattedMessage
                        id="_.credit_card_waitlist.cta_reject"
                        defaultMessage="No thanks, go to dashboard"
                    />
                </button>
                <button
                    onClick={onAccept}
                    className="flex-1 rounded-full px-l py-m text-m font-med text-white transition-colors"
                    style={{ backgroundColor: '#07172b' }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#0a1f3a';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#07172b';
                    }}
                >
                    <FormattedMessage
                        id="_.credit_card_waitlist.cta_accept"
                        defaultMessage="Get your iwoca credit card"
                    />
                </button>
            </div>

            <div className="mt-s flex items-center justify-center gap-xs bg-white px-l py-s">
                <IconShieldCheck style={{ color: '#07172b' }} />
                <Text className="text-xs" style={{ color: '#07172b' }}>
                    <FormattedMessage
                        id="_.credit_card_waitlist.credit_score_notice"
                        defaultMessage="This won't affect your personal credit score"
                    />
                </Text>
            </div>

            <Text className="mt-s px-l pb-l text-center text-xs text-structure-40">
                <FormattedMessage
                    id="_.credit_card_waitlist.privacy_notice"
                    defaultMessage="To apply for the iwoca Credit Card, we will process the company and personal data you provided as part of your Flexi-Loan application to determine eligibility. This processing is further explained in our upcoming privacy policy update, which you can also read in full <link>here</link>. By registering your interest for the credit card, you acknowledge this specific processing will start immediately."
                    values={{
                        link: (chunks) => (
                            <a
                                href="https://www.iwoca.co.uk/compliance/customer-privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline"
                            >
                                {chunks}
                            </a>
                        )
                    }}
                />
            </Text>
        </div>
    );
};
